import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type { Plan } from '@actual-app/core/server/rumbo/plan-app';
import type { TFunction } from 'i18next';

import { Boton } from '#components/mobile/ui/Boton';
import { color, radio, suave, texto } from '#components/mobile/ui/tokens';
import type { UsePlan } from '#hooks/usePlan';

/** Nombre del plan para la fila de Ajustes. */
export function nombrePlan(t: TFunction, plan: Plan): string {
  if (plan === 'cortesia') {
    return t('Founder');
  }
  if (plan === 'altamar') {
    return t('Altamar');
  }
  return t('Free');
}

/**
 * Ajustes → «Tu plan»: qué plan tiene la persona, qué incluye, el «Avísame»
 * del banco y lo que Rumbo promete. Hoy nadie paga: no se vende nada aquí.
 * El estado llega de `usePlan()` en `AjustesRumbo` (una sola petición).
 */
export function PlanSettings({ estado }: { estado: UsePlan }) {
  const { t, i18n } = useTranslation();
  const { plan, cargando, sinServidor, quiereAviso, pedirAviso } = estado;

  if (sinServidor) {
    return (
      <View data-testid="plan-ajustes">
        <Nota>
          <Trans>
            On this phone there are no plans: everything is yours and free.
          </Trans>
        </Nota>
      </View>
    );
  }

  if (!plan) {
    return (
      <View data-testid="plan-ajustes">
        <Nota>
          {cargando ? (
            <Trans>Loading…</Trans>
          ) : (
            <Trans>We could not check your plan. Try again later.</Trans>
          )}
        </Nota>
      </View>
    );
  }

  const frase =
    plan.plan === 'cortesia'
      ? t(
          'You are one of the first 100 people: the cloud and sharing, free of charge while Rumbo grows.',
        )
      : plan.plan === 'altamar'
        ? t('Cloud, sharing and bank included.')
        : t('Everything you need to keep your accounts, on this phone.');

  const fecha =
    plan.hasta !== null
      ? new Date(plan.hasta * 1000).toLocaleDateString(i18n.language, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

  const quiere = quiereAviso('banco');

  const promesas = [
    t('Your data can always be exported, for free.'),
    t('No ads and no loans.'),
    t('A notice 15 days before any renewal.'),
    t('You cancel on the same screen where you sign up.'),
    t('Price rises only apply to people who join later.'),
    t('Rumbo is born from Actual Budget, free software.'),
  ];

  return (
    <View style={{ gap: 14 }} data-testid="plan-ajustes">
      <View
        style={{
          gap: 4,
          padding: 14,
          borderRadius: radio.tarjeta,
          backgroundColor: suave('#1f6f8b', 14),
        }}
      >
        <Text style={{ ...texto.titulo, color: color.fg }}>
          {nombrePlan(t, plan.plan)}
        </Text>
        <Text style={{ ...texto.cuerpo, color: color.fg }}>{frase}</Text>
        {fecha && (
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            {t('Until {{date}}', { date: fecha })}
          </Text>
        )}
      </View>

      <Seccion titulo={t("What's included")}>
        <Bloque>
          <Incluye
            texto={t('Cloud copy')}
            incluido={plan.funciones.nube}
            testId="plan-incluye-nube"
          />
          <Incluye
            texto={t('Share with your partner or family')}
            incluido={plan.funciones.compartir}
            testId="plan-incluye-compartir"
          />
          <Incluye
            texto={t('Bank connection')}
            incluido={plan.funciones.banco}
            testId="plan-incluye-banco"
          />
        </Bloque>
      </Seccion>

      {!plan.funciones.banco && (
        <Bloque>
          <Text style={{ ...texto.fila, color: color.fg }}>
            <Trans>The bank connection is on its way</Trans>
          </Text>
          <Nota>
            <Trans>
              When it is ready, you will be able to read your bank from Rumbo.
              Meanwhile, you can import your transactions.
            </Trans>
          </Nota>
          <Boton
            variante={quiere ? 'fantasma' : 'primario'}
            isDisabled={cargando}
            onPress={() => void pedirAviso('banco', !quiere)}
            data-testid="plan-avisame-banco"
            aria-pressed={quiere}
            style={{ alignSelf: 'flex-start' }}
          >
            {quiere ? t('We will let you know ✓') : t('Notify me')}
          </Boton>
        </Bloque>
      )}

      <Seccion titulo={t('What we promise you')}>
        <Bloque>
          {promesas.map(promesa => (
            <View
              key={promesa}
              style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}
            >
              <Text aria-hidden="true" style={{ color: color.ok }}>
                ✓
              </Text>
              <Text style={{ ...texto.cuerpo, color: color.fg, flex: 1 }}>
                {promesa}
              </Text>
            </View>
          ))}
        </Bloque>
      </Seccion>

      <Nota>
        <Trans>
          Nobody pays anything today. When there is a paid plan, you will see it
          here before anywhere else.
        </Trans>
      </Nota>
    </View>
  );
}

function Incluye({
  texto: nombre,
  incluido,
  testId,
}: {
  texto: string;
  incluido: boolean;
  testId: string;
}) {
  const { t } = useTranslation();
  return (
    <View
      data-testid={testId}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 32,
      }}
    >
      <Text style={{ ...texto.cuerpo, color: color.fg, flex: 1 }}>
        {nombre}
      </Text>
      {incluido ? (
        <Text
          aria-label={t('Included')}
          style={{ ...texto.fila, color: color.ok }}
        >
          ✓
        </Text>
      ) : (
        <Text
          style={{
            ...texto.pequeno,
            color: color.fg2,
            padding: '2px 8px',
            borderRadius: radio.pildora,
            backgroundColor: color.muteSoft,
          }}
        >
          <Trans>Coming soon</Trans>
        </Text>
      )}
    </View>
  );
}

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 6 }}>
      <Text
        role="heading"
        aria-level={3}
        style={{ ...texto.etiqueta, color: color.fg3, paddingLeft: 4 }}
      >
        {titulo}
      </Text>
      {children}
    </View>
  );
}

function Bloque({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        gap: 12,
        padding: 12,
        borderRadius: radio.tarjeta,
        border: `1px solid ${color.line}`,
      }}
    >
      {children}
    </View>
  );
}

function Nota({ children }: { children: ReactNode }) {
  return (
    <Text style={{ ...texto.secundario, color: color.fg2 }}>{children}</Text>
  );
}
